import React, { useState, useCallback } from 'react';
import TextTruncate from 'react-text-truncate';
import cx from 'classnames';
import ApiPoller from '../../../atoms/api-poller';
import ProgressBar from '../../../atoms/progress-bar';
import { pluralise } from '../../../../utils/form-utils';
import { toCurrency } from '../../../../utils/react-utils';
import Logo from '../../../atoms/logo';
import styles from './styles.scss';
import { Button } from '../../../atoms/button';

interface GroupBannerProps {
    logourl: string;
    title: string;
    description: string;
    goal: string;
    query: string;
    token: string;
}

const GroupBanner = function GroupBanner({
    logourl,
    title,
    description,
    goal,
    query,
    token,
} : GroupBannerProps) {
    const [donationSum, setDonationSum] = useState(0);
    const [donationCount, setDonationCount] = useState(0);
    const [showAllDescription, setShowAllDescription] = useState(false);

    const handleshowAllDescription = (seeMore: boolean) => () => {
        setShowAllDescription(seeMore);
    };

    const receiveApiEvent = (response: any, statusCode: number) => {
        if (statusCode !== 200 || response.value.length === 0) {
            return;
        }

        const total = parseFloat(response.value[0].Total);
        const sum = !total ? 0 : total;

        const count = response['@odata.count'];

        setDonationSum(sum);
        setDonationCount(count);
    };

    // use the useCallBack hook to memoize the receiveApiEvent function
    // refer to react/jsx-no-bind eslint error
    const apiPollerCallback = useCallback((response: any, code: number) => receiveApiEvent(response, code), []);

    let descriptionText = null;
    if (!showAllDescription) {
        descriptionText = (
            <TextTruncate
                line={4}
                element="p" /* Description should be in new paragraph below title */
                truncateText="…"
                text={description}
                textTruncateChild={(
                    <Button
                        id="see-more"
                        appearance="link-secondary"
                        onClick={handleshowAllDescription(true)}
                        width="auto"
                    >
                        See More
                    </Button>
                )}
            />
        );
    } else {
        descriptionText = (
            <p>
                <span>{description}</span>
                <Button
                    id="see-less"
                    appearance="link-secondary"
                    onClick={handleshowAllDescription(false)}
                    width="auto"
                >
                    See Less
                </Button>
            </p>
        );
    }

    return (
        <div className={styles['group-banner']}>
            <ApiPoller
                queries={[query]}
                callback={apiPollerCallback}
                token={token}
            />
            <div>
                <div className={styles.logo}>
                    <Logo image={logourl} />
                </div>
            </div>
            <div className={styles['group-column']}>
                <h6 className={styles.title}>{title}</h6>
                {descriptionText}
            </div>
            <div className={styles['donation-column']}>
                <p className={styles.subtitle}>
                    {'Raised '}
                    <span>{toCurrency(donationSum)}</span>
                    {` from ${donationCount} ${pluralise(donationCount, 'donor', 'donors')}`}
                </p>
                <div className={styles['progress-bar']}>
                    <ProgressBar
                        max={parseFloat(goal)}
                        current={parseFloat(`${donationSum}`)}
                    />
                </div>
                <p className={cx(styles.subtitle, styles.goal)}>{`Goal ${toCurrency(goal)}`}</p>
            </div>
        </div>
    );
};

export default GroupBanner;
